import { useState } from "react";
import { TripCoverPhoto, getCoverPhotoUrl } from "@/hooks/useUnsplash";
import { cn } from "@/lib/utils";

interface CoverPhotoBackgroundProps {
  coverPhoto: TripCoverPhoto | null;
  destination?: string | null;
  dateRange?: string | null;
  className?: string;
}

/**
 * Edge-to-edge cover photo background for itinerary views.
 * Includes proper Unsplash attribution.
 */
export function CoverPhotoBackground({
  coverPhoto,
  destination,
  dateRange,
  className,
}: CoverPhotoBackgroundProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Get the sized URL for hero display
  const heroUrl = getCoverPhotoUrl(coverPhoto?.url || null, 'hero');

  // Render gradient placeholder if no cover photo or image failed to load
  if (!coverPhoto || !heroUrl || imageError) {
    return (
      <div
        className={cn(
          "relative w-full h-96 bg-gradient-to-br from-slate-600 via-slate-500 to-slate-400 rounded-t-2xl",
          className
        )}
      >
        {/* Destination and date overlay on placeholder */}
        {(destination || dateRange) && (
          <div className="absolute top-6 left-6 text-white">
            {destination && (
              <h2 className="text-4xl font-bold drop-shadow-lg">{destination}</h2>
            )}
            {dateRange && (
              <p className="text-lg mt-1 drop-shadow-md">{dateRange}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("relative w-full h-96 rounded-t-2xl", className)}>
      {/* Image container with overflow-hidden for rounded corners */}
      <div className="absolute inset-0 overflow-hidden rounded-t-2xl">
        {/* Placeholder gradient while loading */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-600 via-slate-500 to-slate-400 animate-pulse" />
        )}

        {/* Main image */}
        <img
          src={heroUrl}
          alt={coverPhoto.altDescription || "Trip cover photo"}
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity duration-300",
            imageLoaded ? "opacity-100" : "opacity-0"
          )}
          style={{ objectPosition: `center ${coverPhoto.positionY ?? 50}%` }}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />

        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-transparent" />
      </div>

      {/* Destination and date overlay */}
      {(destination || dateRange) && (
        <div className="absolute top-6 left-6 text-white z-10">
          {destination && (
            <h2 className="text-4xl font-bold drop-shadow-lg">{destination}</h2>
          )}
          {dateRange && (
            <p className="text-lg mt-1 drop-shadow-md">{dateRange}</p>
          )}
        </div>
      )}

      {/* Unsplash attribution - positioned in bottom right, above where card overlaps */}
      <a
        href={`https://unsplash.com/@${coverPhoto.photographerUsername}?utm_source=trip_itinerary_builder&utm_medium=referral`}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute bottom-24 right-4 text-xs text-white/80 bg-black/30 backdrop-blur-sm rounded px-2 py-1 hover:bg-black/50 transition-colors z-10"
      >
        Photo by{" "}
        <span className="underline">{coverPhoto.photographerName}</span>
        {" on "}
        <span className="underline">Unsplash</span>
      </a>
    </div>
  );
}
